import { ArrowDropDown, FilterAlt } from "@mui/icons-material";
import { IconButton, Menu, Tooltip, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import { FormikValues, useFormikContext } from "formik";
import { FC, MouseEvent, useEffect, useState } from "react";

import Button from "../../../common/Button";
import DropdownField from "../../../common/form/dropdown/DropdownField";
import FilterSecondPopup from "./FilterSecondPopup";

import patientService from "../../../../services/patient";

import { districts, fileExportDropDown } from "../../../../constants/patient";
import { userRoles } from "../../../../constants/user";
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from "../../../../redux/store";
import { setLoadingAction } from "../../../../redux/utils/actions";
import { PAGE_SIZE } from "../../../../utils/pageination";
import DropdownFieldMultiselect from "../../../common/form/dropdown/DropdownFieldMultiselect";
import { patientSx } from "../styles";
import styles from "../styles.module.scss";
import { patientTableInitData } from "../utils";

interface IFilterPopup {
  setPageNo: (pageNo: number) => void;
  getPatientTableData: (pageNo: number, pageSize: number) => void;
  getPatientTableDataWithoutFilter: () => void;
}

const FilterPopup: FC<IFilterPopup> = (props) => {
  const { getPatientTableData, getPatientTableDataWithoutFilter, setPageNo } =
    props;

  const [filterType, setFilterType] = useState("");
  const [filterAllStatus, setFilterAllStatus] = useState([]);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [isShowUserSelectModal, setIsShowUserSelectModal] =
    useState<null | HTMLElement>(null);

  const dispatch = useAppDispatch();

  const { values, setValues, setFieldValue } = useFormikContext<FormikValues>();
  const { role } = useAppSelector(
    (state: RootState) => state.auth.profileData.user
  );

  const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const getFilterAndDistrictInfo = async () => {
    try {
      dispatch(setLoadingAction(true));
      const info = await patientService.filter();
      const createdByModified = info.data.data.createdBy.map((item: any) => ({
        ...item,
        isChecked: false,
      }));
      dispatch(setLoadingAction(false));
      const approvedByModified = info.data.data.approvedBy?.map(
        (item: any) => ({ ...item, isChecked: false })
      );
      setFieldValue("createdBy", createdByModified);
      setFieldValue("approvedBy", approvedByModified);
      const statusWithCount = info.data.data.statusWithCount?.map(
        (item: any) => ({
          label: `${item.status} (${item.noOfPatients})`,
          value: item.status,
          name: item.status,
        })
      );
      setFilterAllStatus(statusWithCount);
    } catch (e) {
      dispatch(setLoadingAction(false));
    }
  };

  const getFilteredPatientTableData = async (values: FormikValues) => {
    getPatientTableData(0, PAGE_SIZE);
    getFilterAndDistrictInfo();
    setPageNo(0);
    setAnchorElUser(null);
  };

  const handleOpenUserSelectModal =
    (filterType: string) => (event: MouseEvent<HTMLElement>) => {
      setIsShowUserSelectModal(event.currentTarget);
      setFilterType(filterType);
    };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleCloseUserSelectModal = () => {
    setIsShowUserSelectModal(null);
  };

  const inputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue(e.target.name, e.target.value);
  };

  const resetFilter = async () => {
    const newValues = {
      ...patientTableInitData,
      createdBy: values.createdBy.map((item: any) => ({
        ...item,
        isChecked: false,
      })),
      approvedBy: values.approvedBy.map((item: any) => ({
        ...item,
        isChecked: false,
      })),
      csvType: fileExportDropDown[role][0]._id,
      fromDateCSV: "2023-02-23",
      toDateCSV: new Date().toISOString().substring(0, 10),
    };

    setValues(newValues);
    getPatientTableDataWithoutFilter();
  };

  useEffect(() => {
    const getFilterInfo = async () => {
      try {
        dispatch(setLoadingAction(true));
        const info = await patientService.filter();
        const createdByModified = info.data.data.createdBy.map((item: any) => ({
          ...item,
          isChecked: false,
        }));
        dispatch(setLoadingAction(false));
        const approvedByModified = info.data.data.approvedBy?.map(
          (item: any) => ({ ...item, isChecked: false })
        );
        setFieldValue("createdBy", createdByModified);
        setFieldValue("approvedBy", approvedByModified);
        const statusWithCount = info.data.data.statusWithCount?.map(
          (item: any) => ({
            label: `${item.status} (${item.noOfPatients})`,
            value: item.status,
            name: item.status,
          })
        );
        setFilterAllStatus(statusWithCount);
      } catch (e) {
        dispatch(setLoadingAction(false));
      }
    };

    if (anchorElUser) {
      getFilterInfo();
    }
  }, [setFieldValue, dispatch, anchorElUser]);

  useEffect(() => {
    setFieldValue("csvType", fileExportDropDown[role][0]._id);
  }, []);

  return (
    <>
      {/* <p></p> */}
      <Box>
        <Tooltip title="Open settings">
          <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
            {/* <Avatar alt="Manoj" src="/static/images/avatar/2.jpg" /> */}
            <FilterAlt />
          </IconButton>
        </Tooltip>
      </Box>
      <Menu
        id="menu-appbar"
        anchorEl={anchorElUser}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={Boolean(anchorElUser)}
        onClose={handleCloseUserMenu}
      >
        <Box sx={{ MaxWidth: "400px" }}>
          <Typography sx={{ padding: "10px 20px" }}>
            <b>Filter Option</b>
          </Typography>
          <hr />
          <Box sx={{ padding: "10px 20px" }}>
            <Box>
              <Typography sx={patientSx.filterSpan}>Status</Typography>
              <DropdownFieldMultiselect
                dropDownList={filterAllStatus}
                value={values?.status}
                label={"Status"}
                name={"status"}
                onChange={(e) => setFieldValue("status", e.target.value)}
                itemFieldValue="name"
                itemFieldLabel="label"
                multiple
              />
            </Box>
            <Box>
              <Typography sx={patientSx.filterSpan}>District</Typography>
              <DropdownField
                name="district"
                label="District Information"
                dropDownList={districts.map((d) => ({ _id: d, name: d }))}
                itemFieldValue="_id"
                itemFieldLabel="name"
                value={values?.district}
                onChange={inputHandler}
              />
            </Box>
            <Box sx={{ paddingTop: "15px" }}>
              <Typography sx={patientSx.filterSpan}>Raised Date</Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", margin: "0 -5px" }}>
                <Box
                  sx={{
                    width: "50%",
                    padding: "0 5px",
                    "@media (max-width: 767px)": {
                      width: "100%",
                      padding: "0",
                    },
                  }}
                >
                  <Typography sx={patientSx.filterSpan}>From</Typography>
                  <input
                    type="date"
                    className="form-control"
                    name="fromDate"
                    placeholder="From Date"
                    value={values?.fromDate}
                    onChange={inputHandler}
                    style={{ width: "100%" }}
                  />
                </Box>
                <Box
                  sx={{
                    width: "50%",
                    padding: "0 5px",
                    "@media (max-width: 767px)": {
                      width: "100%",
                      padding: "0",
                    },
                  }}
                >
                  <Typography sx={patientSx.filterSpan}>To</Typography>
                  <input
                    type="date"
                    className="form-control"
                    name="toDate"
                    placeholder="To Date"
                    value={values?.toDate}
                    onChange={inputHandler}
                    style={{ width: "100%" }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
          {[userRoles.SUPER_ADMIN, userRoles.D_C, userRoles.STAFF].includes(
            role
          ) && (
            <Box sx={{ padding: "10px 20px" }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", margin: "0 -5px" }}>
                <Box
                  sx={{
                    width: "50%",
                    padding: "0 5px",
                    "@media (max-width: 767px)": {
                      width: "100%",
                      padding: "0",
                    },
                  }}
                >
                  <Typography sx={patientSx.filterSpan}>Created By</Typography>
                  <div
                    className={styles.falseDDL}
                    style={{ width: "100%" }}
                    onClick={handleOpenUserSelectModal("createdBy")}
                  >
                    <p className={styles.ddlText}>All</p>
                    <ArrowDropDown />
                  </div>
                </Box>

                <Box
                  sx={{
                    width: "50%",
                    padding: "0 5px",
                    "@media (max-width: 767px)": {
                      width: "100%",
                      padding: "0",
                    },
                  }}
                >
                  <Typography sx={patientSx.filterSpan}>Approved By</Typography>
                  <div
                    className={styles.falseDDL}
                    style={{ width: "100%" }}
                    onClick={handleOpenUserSelectModal("approvedBy")}
                  >
                    <p className={styles.ddlText}>All</p>
                    <ArrowDropDown />
                  </div>
                </Box>
              </Box>
            </Box>
          )}

          <Box
            sx={{
              padding: "15px 20px 20px",
              position: "relative",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Button color="default" type="button" onClick={resetFilter}>
              Reset
            </Button>
            <Button
              color="alert"
              type="button"
              onClick={() => {
                getFilteredPatientTableData(values);
              }}
            >
              Apply
            </Button>
          </Box>
        </Box>
        <FilterSecondPopup
          filterType={filterType}
          isShowUserSelectModal={isShowUserSelectModal}
          setIsShowUserSelectModal={setIsShowUserSelectModal}
          handleCloseUserSelectModal={handleCloseUserSelectModal}
        />
      </Menu>
    </>
  );
};

export default FilterPopup;
